"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql } from "@opensea/graphql";
import { EmptyState } from "@opensea/ui-kit";
import { CurrencyStatsSortBy } from "@opensea/utils";
import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { useIsOwnProfileAddress, useProfileAddress } from "@/components/profile/ProfileAddressProvider";
import { useHasSearchFilters } from "@/components/search/hooks";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { usePaginatedQuery } from "@/hooks/api/use-paginated-query";
import { QUERY_PARAM_KEYS } from "@/hooks/query-param/keys";
import { useChainsQueryParam } from "@/hooks/query-param/use-chains-query-param";
import { useCurrencyCategoryQueryParam } from "@/hooks/query-param/use-currency-category-query-param";
import { useCurrencyStatsSortByQueryParam } from "@/hooks/query-param/use-currency-stats-sort-by-query-param";
import { useHasMerchandisingQueryParam } from "@/hooks/query-param/use-has-merchandising-query-param";
import { useHasNftQueryParam } from "@/hooks/query-param/use-has-nft-query-param";
import { usePriceQueryParam } from "@/hooks/query-param/use-price-query-param";
import { useSearchQueryQueryParam } from "@/hooks/query-param/use-search-query-query-param";
import { useSortDirectionQueryParam } from "@/hooks/query-param/use-sort-direction-query-param";
import { useStatusQueryParam } from "@/hooks/query-param/use-status-query-param";
import { useSubscription } from "@/hooks/use-subscription";
import { topStickyVariants } from "@/styles/utils/sticky";
import { CurrencyStatsTable } from "../CurrencyStatsTable";
import { getCurrencySortBy } from "../CurrencyStatsTable/utils";
import { ProfileCurrencyStatsTableRow, ProfileCurrencyStatsTableRowFragment } from "./ProfileCurrencyStatsTableRow";
import { ProfileCurrencyStatsTableSkeleton } from "./ProfileCurrencyStatsTableSkeleton";
import { ProfileTokensActionBar } from "./ProfileTokensActionBar/ProfileTokensActionBar";
import { ProfileTokensCountProvider } from "./ProfileTokensCountProvider";
import { ProfileTokensSelectionProvider, useProfileTokensSelectionStore } from "./ProfileTokensSelectionProvider";
const query = graphql(`
    query ProfileCurrencyStatsTablePaginatedQuery(
      $addresses: [Address!]!
      $filter: CurrenciesFilter!
      $sort: CurrenciesSort
      $limit: Int!
      $cursor: String
    ) {
      currencyOwnerships(
        addresses: $addresses
        filter: $filter
        sort: $sort
        limit: $limit
        cursor: $cursor
      ) {
        items {
          ...ProfileCurrencyStatsTableRow
          usdValue
          currency {
            id
            name
            symbol
            usdPrice
            stats {
              marketCapUsd
              oneDay {
                volume
                priceChange
              }
              sevenDay {
                priceChange
              }
            }
          }
        }
        nextPageCursor
        totalCount
      }
    }
  `, [ProfileCurrencyStatsTableRowFragment]);
const subscriptionQuery = graphql(`
  subscription currencyOwnershipsSubscription(
    $address: Address!
    $filter: CurrenciesFilter!
  ) {
    currencyOwnershipUpdatesV2(address: $address, filter: $filter) {
      quantity
      usdValue
      currency {
        id
        name
        symbol
        usdPrice
        stats {
          marketCapUsd
          oneDay {
            volume
            priceChange
          }
          sevenDay {
            priceChange
          }
        }
      }
      ...ProfileCurrencyStatsTableRow
    }
  }
`, [ProfileCurrencyStatsTableRowFragment]);
const DEFAULT_SORT = {
  by: CurrencyStatsSortBy.USD_VALUE,
  direction: "DESC"
};
const PAGE_SIZE = 25;
function ProfileCurrencyStatsTablePaginatedBase(t0) {
  const $ = _c(73);
  let rest;
  if ($[0] !== t0) {
    const {
      persona: _persona,
      ...t1
    } = t0;
    rest = t1;
    $[0] = t0;
    $[1] = rest;
  } else {
    rest = $[1];
  }
  const address = useProfileAddress();
  const isOwnProfileAddress = useIsOwnProfileAddress();
  const hasFilters = useHasSearchFilters();
  const {
    searchQuery
  } = useSearchQueryQueryParam();
  const {
    chains
  } = useChainsQueryParam();
  const {
    category
  } = useCurrencyCategoryQueryParam();
  const {
    price: marketCap
  } = usePriceQueryParam(QUERY_PARAM_KEYS.marketCap);
  const {
    hasNft
  } = useHasNftQueryParam();
  const {
    hasMerchandising
  } = useHasMerchandisingQueryParam();
  const {
    statuses: rawStatuses,
    removeStatus
  } = useStatusQueryParam();
  let t1;
  if ($[2] !== isOwnProfileAddress || $[3] !== rawStatuses) {
    t1 = isOwnProfileAddress ? rawStatuses : rawStatuses.filter(_temp);
    $[2] = isOwnProfileAddress;
    $[3] = rawStatuses;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const statuses = t1;
  let t2;
  let t3;
  if ($[5] !== isOwnProfileAddress || $[6] !== rawStatuses || $[7] !== removeStatus) {
    t2 = () => {
      if (!isOwnProfileAddress && rawStatuses.includes("hidden")) {
        removeStatus("hidden");
      }
    };
    t3 = [isOwnProfileAddress, rawStatuses, removeStatus];
    $[5] = isOwnProfileAddress;
    $[6] = rawStatuses;
    $[7] = removeStatus;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  const {
    sortBy
  } = useCurrencyStatsSortByQueryParam(DEFAULT_SORT.by);
  const {
    sortDirection
  } = useSortDirectionQueryParam(DEFAULT_SORT.direction);
  let t4;
  if ($[10] !== sortBy) {
    t4 = getCurrencySortBy(sortBy);
    $[10] = sortBy;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== sortDirection || $[13] !== t4) {
    t5 = {
      direction: sortDirection,
      by: t4
    };
    $[12] = sortDirection;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const sort = t5;
  const [setAllTokens] = useProfileTokensSelectionStore(useShallow(_temp2));
  const t6 = chains.length > 0 ? chains : undefined;
  let t7;
  if ($[15] !== marketCap) {
    t7 = marketCap ? {
      min: marketCap.min,
      max: marketCap.max
    } : undefined;
    $[15] = marketCap;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = hasNft || undefined;
  const t9 = hasMerchandising || undefined;
  let t10;
  if ($[17] !== isOwnProfileAddress || $[18] !== statuses) {
    t10 = isOwnProfileAddress ? statuses.includes("hidden") : undefined;
    $[17] = isOwnProfileAddress;
    $[18] = statuses;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== category || $[21] !== t10 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t11 = {
      chains: t6,
      coinGeckoCategory: category,
      marketCapRange: t7,
      hasNft: t8,
      hasMerchandising: t9,
      hiddenOnly: t10
    };
    $[20] = category;
    $[21] = t10;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const filter = t11;
  let t12;
  if ($[27] !== address) {
    t12 = [address];
    $[27] = address;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== filter || $[30] !== sort || $[31] !== t12) {
    t13 = {
      addresses: t12,
      sort,
      filter,
      limit: PAGE_SIZE
    };
    $[29] = filter;
    $[30] = sort;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = !address;
  let t15;
  if ($[33] !== t13 || $[34] !== t14) {
    t15 = {
      query,
      variables: t13,
      pause: t14
    };
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      pageSize: PAGE_SIZE,
      throwOnError: false
    };
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const {
    items: paginatedItems,
    pagination,
    error,
    refetch,
    data: currencyOwnershipsData
  } = usePaginatedQuery(t15, _temp3, t16);
  let t17;
  let t18;
  if ($[37] !== isOwnProfileAddress || $[38] !== paginatedItems || $[39] !== setAllTokens) {
    t17 = function updateSelectionStore() {
      if (isOwnProfileAddress && paginatedItems.length > 0) {
        setAllTokens(paginatedItems.filter(_temp4));
      }
    };
    t18 = [paginatedItems, isOwnProfileAddress, setAllTokens];
    $[37] = isOwnProfileAddress;
    $[38] = paginatedItems;
    $[39] = setAllTokens;
    $[40] = t17;
    $[41] = t18;
  } else {
    t17 = $[40];
    t18 = $[41];
  }
  useEffect(t17, t18);
  let t19;
  if ($[42] !== refetch) {
    t19 = () => {
      refetch({
        requestPolicy: "network-only"
      });
    };
    $[42] = refetch;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const refetchData = t19;
  let t20;
  if ($[44] !== address || $[45] !== filter) {
    t20 = {
      address,
      filter
    };
    $[44] = address;
    $[45] = filter;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const t21 = !address;
  let t22;
  if ($[47] !== t20 || $[48] !== t21) {
    t22 = {
      query: subscriptionQuery,
      variables: t20,
      pause: t21
    };
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== refetchData) {
    t23 = _ => {
      refetchData();
    };
    $[50] = refetchData;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  useSubscription(t22, t23);
  let t24;
  if ($[52] !== paginatedItems || $[53] !== searchQuery) {
    let t25;
    if ($[55] !== searchQuery) {
      t25 = item_0 => item_0?.currency.name.toLowerCase().includes(searchQuery?.toLowerCase() ?? "") || item_0?.currency.symbol.toLowerCase().includes(searchQuery?.toLowerCase() ?? "");
      $[55] = searchQuery;
      $[56] = t25;
    } else {
      t25 = $[56];
    }
    t24 = paginatedItems.filter(t25);
    $[52] = paginatedItems;
    $[53] = searchQuery;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const tableItems = t24;
  if (error || tableItems.length === 0 && !pagination.hasNext) {
    const t25 = error ? "no-data" : hasFilters ? "no-results-with-filters" : "no-results";
    let t26;
    if ($[57] !== t25) {
      t26 = <EmptyState size="lg" variant={t25} />;
      $[57] = t25;
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    return t26;
  }
  const t25 = currencyOwnershipsData?.currencyOwnerships.totalCount || tableItems.length;
  const t26 = pagination.hasNext;
  const t27 = hasFilters ? "after-hero-header-search-pills" : "after-hero-header-toolbar";
  let t28;
  if ($[59] !== t27) {
    t28 = topStickyVariants({
      variant: t27,
      tableHeader: true
    });
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== pagination.hasNext || $[62] !== pagination.loadNext || $[63] !== rest || $[64] !== t28 || $[65] !== tableItems) {
    t29 = <CurrencyStatsTable defaultSortBy={DEFAULT_SORT.by} hasNext={t26} headerClassName={t28} itemKey={_temp5} items={tableItems} loadNext={pagination.loadNext} profile={true} renderRow={CurrencyStatsTableRow} {...rest} />;
    $[61] = pagination.hasNext;
    $[62] = pagination.loadNext;
    $[63] = rest;
    $[64] = t28;
    $[65] = tableItems;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== refetchData) {
    t30 = <ProfileTokensActionBar onRefetch={refetchData} />;
    $[67] = refetchData;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t25 || $[70] !== t29 || $[71] !== t30) {
    t31 = <ProfileTokensCountProvider fetching={false} hasMore={false} totalCount={t25}>{t29}{t30}</ProfileTokensCountProvider>;
    $[69] = t25;
    $[70] = t29;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  return t31;
}
function _temp5(item_1) {
  return item_1?.currency.id;
}
function _temp4(item) {
  return item !== undefined;
}
function _temp3(data) {
  return data?.currencyOwnerships;
}
function _temp2(state) {
  return [state.setAllTokens];
}
function _temp(s) {
  return s !== "hidden";
}
export const ProfileCurrencyStatsTablePaginated = withSuspense(function ProfileCurrencyStatsTablePaginated(props) {
  const isOwnProfileAddress = useIsOwnProfileAddress();
  if (isOwnProfileAddress) {
    return <ProfileTokensSelectionProvider>
          <ProfileCurrencyStatsTablePaginatedBase {...props} />
        </ProfileTokensSelectionProvider>;
  }
  return <ProfileCurrencyStatsTablePaginatedBase {...props} />;
}, {
  fallback: ({
    persona
  }) => <ProfileCurrencyStatsTableSkeleton persona={persona} />,
  errorFallback: <EmptyState size="lg" variant="no-data" />,
  ssr: false
});
function CurrencyStatsTableRow(t0) {
  const $ = _c(3);
  const {
    item,
    index
  } = t0;
  const t1 = index + 1;
  let t2;
  if ($[0] !== item || $[1] !== t1) {
    t2 = <ProfileCurrencyStatsTableRow currencyBalance={item} rank={t1} />;
    $[0] = item;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}